import React, { useState, useEffect } from 'react';
import { generateClient } from 'aws-amplify/data';
import { Announcement, Event } from '../types';
import SponsorBanner from './SponsorBanner';
import TideWeatherWidget from './member/TideWeatherWidget';
import StorageImage from './StorageImage';
import GroperPinnedCard from './GroperPinnedCard';
import { GROPER_ARTICLE_PATH } from '../content/groperStory';
import ColouringCompCard from './ColouringCompCard';
import UpcomingEventRow, { UpcomingEvent, eventListedUntil } from './UpcomingEventRow';
import NextEventStrip from './NextEventStrip';
import { COLOURING_PATH, isColouringCompOpen } from '../content/colouringComp';
import { formatClubDate } from '../utils/clubTime';

/** Upcoming Events rows shown on the landing page. */
const LANDING_EVENT_LIMIT = 3;
/** How often the Upcoming Events rows re-check their state. */
const EVENT_RECHECK_MS = 60 * 1000;

/** An event stays on the list until it ends, not just until it starts, so the
 *  "Check in now" / "Fishing now" row is there on the morning. */
function eventStillListed(e: UpcomingEvent, now: number): boolean {
  return eventListedUntil(e) >= now;
}

// Use apiKey auth mode for guest access to public content
const client = generateClient({ authMode: 'apiKey' });

interface LandingPageProps {
  onNavigateToAdmin?: () => void;
  onNavigateToRegister?: () => void;
  onNavigateToMission?: () => void;
  onNavigateToRules?: () => void;
  onNavigateToConduct?: () => void;
  onNavigateToWaiver?: () => void;
  onNavigateToDashboard?: () => void;
  onNavigateToLearnMore?: () => void;
  onNavigateToAnnouncementDetail?: (id: string) => void;
  onNavigateToGroperArticle?: () => void;
  onNavigateToColouring?: () => void;
}

const LandingPage: React.FC<LandingPageProps> = ({ onNavigateToRegister, onNavigateToMission, onNavigateToRules, onNavigateToConduct, onNavigateToWaiver, onNavigateToDashboard, onNavigateToLearnMore, onNavigateToAnnouncementDetail, onNavigateToGroperArticle, onNavigateToColouring }) => {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  // Clock for the Upcoming Events rows. Re-checked every minute, and as soon
  // as the tab becomes visible again (phones suspend timers in the
  // background), so a row flips to "Check in now" / "Fishing now" and drops
  // off after it ends without a reload.
  const [nowTick, setNowTick] = useState(() => Date.now());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [quickLinksOpen, setQuickLinksOpen] = useState(false);
  const [mobileQuickLinksOpen, setMobileQuickLinksOpen] = useState(false);
  const quickLinksRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadContent();
  }, []);

  useEffect(() => {
    const tick = () => setNowTick(Date.now());
    const id = window.setInterval(tick, EVENT_RECHECK_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') tick();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  // Close the desktop Quick Links dropdown when clicking outside it
  useEffect(() => {
    if (!quickLinksOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (quickLinksRef.current && !quickLinksRef.current.contains(e.target as Node)) {
        setQuickLinksOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [quickLinksOpen]);

  const loadContent = async () => {
    try {
      setLoading(true);
      setError(null);

      // Max items to show on the landing page. Anything beyond this sits on
      // the full /announcements and /events pages.
      const LANDING_LIMIT = 3;
      // Announcements older than this are de-prioritised unless pinned.
      const ANNOUNCEMENT_STALE_DAYS = 90;
      const now = Date.now();
      const staleCutoff = now - ANNOUNCEMENT_STALE_DAYS * 24 * 60 * 60 * 1000;

      // Fetch a larger slice than we render — client-side filtering may drop
      // stale/past items, so we pull extra and then slice down.
      const announcementsResult = await client.models.Announcement.list({
        filter: { isActive: { eq: true } },
        limit: 25,
      });

      const eventsResult = await client.models.Event.list({
        filter: { isActive: { eq: true } },
        limit: 25,
      });

      if (eventsResult.errors) {
        console.error('Events query errors:', eventsResult.errors);
      }

      if (announcementsResult.data) {
        const filtered = (announcementsResult.data as Announcement[]).filter(
          (a) => a.isPinned || new Date(a.publishDate).getTime() >= staleCutoff,
        );
        const sorted = filtered.sort((a, b) => {
          // Pinned first, then most recent publishDate first.
          if (!!a.isPinned !== !!b.isPinned) return a.isPinned ? -1 : 1;
          return new Date(b.publishDate).getTime() - new Date(a.publishDate).getTime();
        });
        setAnnouncements(sorted.slice(0, LANDING_LIMIT) as Announcement[]);
      }

      if (eventsResult.data) {
        // Keep everything not yet ended, sorted; the render step re-filters
        // against the ticking clock and takes the first LANDING_EVENT_LIMIT.
        const filtered = (eventsResult.data as UpcomingEvent[]).filter((e) => eventStillListed(e, now));
        const sorted = filtered.sort(
          (a, b) => new Date(a.eventDate).getTime() - new Date(b.eventDate).getTime(),
        );
        setEvents(sorted as Event[]);
      }

    } catch (err) {
      console.error('Error loading content:', err);
      setError('Failed to load content. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return formatClubDate(date, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };


  const visibleEvents = (events as UpcomingEvent[])
    .filter((e) => eventStillListed(e, nowTick))
    .slice(0, LANDING_EVENT_LIMIT);

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-blue-100">
      {/* Navigation — sits at the very top of the page, sticky throughout */}
      <nav className="bg-white shadow-md sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex justify-between items-center py-2.5">
            <div className="flex items-center">
              <img 
                src="/tumbulgum-logo.png" 
                alt="Tumbulgum Fishing Club" 
                className="h-16 w-auto"
              />
            </div>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-4 lg:space-x-8">
              <a href="#announcements" className="text-gray-700 hover:text-fishing-blue">Announcements</a>
              <a href="#events" className="text-gray-700 hover:text-fishing-blue">Events</a>
              <a href="#sponsors" className="text-gray-700 hover:text-fishing-blue">Sponsors</a>
              <a href="#contact" className="text-gray-700 hover:text-fishing-blue">Contact</a>
              <div className="relative" ref={quickLinksRef}>
                <button
                  onClick={() => setQuickLinksOpen(!quickLinksOpen)}
                  className="flex min-h-[44px] items-center gap-1 whitespace-nowrap text-gray-700 hover:text-fishing-blue"
                  aria-expanded={quickLinksOpen}
                  aria-haspopup="true"
                >
                  Quick Links
                  <svg className={`h-4 w-4 transition-transform ${quickLinksOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {quickLinksOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                    <button onClick={() => { setQuickLinksOpen(false); window.history.pushState({}, '', '/learn-more'); window.dispatchEvent(new PopStateEvent('popstate')); }} className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-fishing-blue">About Us</button>
                    <button onClick={() => { setQuickLinksOpen(false); onNavigateToMission?.(); }} className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-fishing-blue">Mission, Values & Vision</button>
                    <button onClick={() => { setQuickLinksOpen(false); onNavigateToRules?.(); }} className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-fishing-blue">Club Rules</button>
                    <button onClick={() => { setQuickLinksOpen(false); onNavigateToConduct?.(); }} className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-fishing-blue">Code of Conduct</button>
                    <button onClick={() => { setQuickLinksOpen(false); onNavigateToWaiver?.(); }} className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-fishing-blue">Waiver & Release</button>
                    <a href="/Constitution-Tumbulgum-Fishing-Club-V5.pdf" target="_blank" rel="noopener noreferrer" onClick={() => setQuickLinksOpen(false)} className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-fishing-blue">Club Constitution</a>
                    <button onClick={() => { setQuickLinksOpen(false); window.history.pushState({}, '', '/regulations'); window.dispatchEvent(new PopStateEvent('popstate')); }} className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-fishing-blue">NSW Fishing Regulations</button>
                    <button onClick={() => { setQuickLinksOpen(false); window.history.pushState({}, '', '/privacy'); window.dispatchEvent(new PopStateEvent('popstate')); }} className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-fishing-blue">Privacy Policy</button>
                  </div>
                )}
              </div>
              <button 
                onClick={onNavigateToDashboard}
                className="flex min-h-[44px] items-center gap-2 bg-[#1d2e61] hover:bg-[#162349] text-white px-5 py-2 rounded-full font-semibold shadow-sm transition-colors"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
                </svg>
                MyLogin
              </button>
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden flex items-center gap-2">
              <button
                onClick={onNavigateToDashboard}
                className="flex min-h-[44px] items-center gap-1.5 bg-[#1d2e61] hover:bg-[#162349] text-white px-3 py-1.5 rounded-full text-sm font-semibold shadow-sm transition-colors"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
                </svg>
                MyLogin
              </button>
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="flex min-h-[44px] min-w-[44px] items-center justify-center text-gray-700 hover:text-fishing-blue focus:outline-none"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  {mobileMenuOpen ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  )}
                </svg>
              </button>
            </div>
          </div>

          {/* Mobile Navigation Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden pb-3 space-y-1">
              <a 
                href="#announcements" 
                onClick={() => setMobileMenuOpen(false)}
                className="block px-4 py-2 text-gray-700 hover:bg-gray-100 hover:text-fishing-blue rounded"
              >
                Announcements
              </a>
              <a 
                href="#events" 
                onClick={() => setMobileMenuOpen(false)}
                className="block px-4 py-2 text-gray-700 hover:bg-gray-100 hover:text-fishing-blue rounded"
              >
                Events
              </a>
              <a 
                href="#sponsors" 
                onClick={() => setMobileMenuOpen(false)}
                className="block px-4 py-2 text-gray-700 hover:bg-gray-100 hover:text-fishing-blue rounded"
              >
                Sponsors
              </a>
              <a 
                href="#contact" 
                onClick={() => setMobileMenuOpen(false)}
                className="block px-4 py-2 text-gray-700 hover:bg-gray-100 hover:text-fishing-blue rounded"
              >
                Contact
              </a>
              <button
                onClick={() => setMobileQuickLinksOpen(!mobileQuickLinksOpen)}
                className="flex items-center justify-between w-full px-4 py-2 text-gray-700 hover:bg-gray-100 hover:text-fishing-blue rounded"
                aria-expanded={mobileQuickLinksOpen}
              >
                Quick Links
                <svg className={`h-4 w-4 transition-transform ${mobileQuickLinksOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {mobileQuickLinksOpen && (
                <div className="pl-4 space-y-1">
                  <button onClick={() => { setMobileMenuOpen(false); setMobileQuickLinksOpen(false); window.history.pushState({}, '', '/learn-more'); window.dispatchEvent(new PopStateEvent('popstate')); }} className="block w-full text-left px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 hover:text-fishing-blue rounded">About Us</button>
                  <button onClick={() => { setMobileMenuOpen(false); setMobileQuickLinksOpen(false); onNavigateToMission?.(); }} className="block w-full text-left px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 hover:text-fishing-blue rounded">Mission, Values & Vision</button>
                  <button onClick={() => { setMobileMenuOpen(false); setMobileQuickLinksOpen(false); onNavigateToRules?.(); }} className="block w-full text-left px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 hover:text-fishing-blue rounded">Club Rules</button>
                  <button onClick={() => { setMobileMenuOpen(false); setMobileQuickLinksOpen(false); onNavigateToConduct?.(); }} className="block w-full text-left px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 hover:text-fishing-blue rounded">Code of Conduct</button>
                  <button onClick={() => { setMobileMenuOpen(false); setMobileQuickLinksOpen(false); onNavigateToWaiver?.(); }} className="block w-full text-left px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 hover:text-fishing-blue rounded">Waiver & Release</button>
                  <a href="/Constitution-Tumbulgum-Fishing-Club-V5.pdf" target="_blank" rel="noopener noreferrer" onClick={() => { setMobileMenuOpen(false); setMobileQuickLinksOpen(false); }} className="block px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 hover:text-fishing-blue rounded">Club Constitution</a>
                  <button onClick={() => { setMobileMenuOpen(false); setMobileQuickLinksOpen(false); window.history.pushState({}, '', '/regulations'); window.dispatchEvent(new PopStateEvent('popstate')); }} className="block w-full text-left px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 hover:text-fishing-blue rounded">NSW Fishing Regulations</button>
                  <button onClick={() => { setMobileMenuOpen(false); setMobileQuickLinksOpen(false); window.history.pushState({}, '', '/privacy'); window.dispatchEvent(new PopStateEvent('popstate')); }} className="block w-full text-left px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 hover:text-fishing-blue rounded">Privacy Policy</button>
                </div>
              )}
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <div className="relative bg-fishing-blue text-white py-16 md:py-20 px-4 overflow-hidden min-h-[360px] md:min-h-[420px]">
        {/*
          Background image — the Tweed at Tumbulgum with Wollumbin behind.
          Replaced club-photo.jpg, which was 259x194 stretched to full width.
          Source 1920x1080, AI-upscaled 2x (Upscayl, high-fidelity-4x) to
          3840x2160, exported at five widths so each device downloads only what
          it needs.

          <img srcset> rather than a CSS background-image: the browser picks the
          width for the screen and pixel density, and fetchpriority="high" lets
          it start immediately — this is the largest element on the landing
          page, so it sets LCP. Decorative (the heading carries the meaning), so
          alt="".
        */}
        <picture>
          <source
            type="image/webp"
            srcSet="/hero/tumbulgum-river-768.webp 768w, /hero/tumbulgum-river-1280.webp 1280w, /hero/tumbulgum-river-1920.webp 1920w, /hero/tumbulgum-river-2560.webp 2560w, /hero/tumbulgum-river-3840.webp 3840w"
            sizes="100vw"
          />
          <img
            src="/hero/tumbulgum-river-1920.jpg"
            alt=""
            aria-hidden="true"
            fetchPriority="high"
            decoding="async"
            className="absolute inset-0 w-full h-full object-cover object-center"
          />
        </picture>
        {/*
          Overlay lightened from 60/50/70%. That darkness suited a blurry photo
          but greys out this one's sky. Still paired with the existing text
          drop shadows; recheck readability on a phone in sunlight.
        */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/40 to-black/55" />
        
        {/* Content */}
        <div className="relative max-w-6xl mx-auto text-center z-10">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4 text-white drop-shadow-lg">
            Tumbulgum Fishing Club
          </h1>
          <p className="text-xl md:text-2xl mb-6 text-white drop-shadow-md">
            Community, Catches & Conservation
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={onNavigateToRegister}
              className="bg-fishing-green hover:bg-green-600 text-white px-8 py-3 rounded-lg font-semibold transition-colors shadow-lg"
            >
              Join Our Club
            </button>
            <button onClick={onNavigateToLearnMore} className="bg-white hover:bg-gray-100 text-fishing-blue px-8 py-3 rounded-lg font-semibold transition-colors shadow-lg">
              Learn More
            </button>
          </div>
        </div>
      </div>

      {/* Next competition strip (#102): only the next competition, under the hero. */}
      <NextEventStrip events={events as UpcomingEvent[]} now={nowTick} />

      {/* Announcements Section */}
      <section id="announcements" className="py-6 md:py-8 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-4 text-gray-800">
            Club Announcements
          </h2>

          {/* The parent owns spacing between cards (#117): gap-4, no mb-* on cards. */}
          <div className="flex flex-col gap-4">
            {/* Pinned slot: the static CLUB HISTORY "Tumbulgum's Groper" card.
                It replaced the DB-driven pinned spotlight, so it always renders,
                whatever the loading/error/empty state of the DB announcements. */}
            <GroperPinnedCard
              onReadMore={() => {
                if (onNavigateToGroperArticle) {
                  onNavigateToGroperArticle();
                } else {
                  window.history.pushState({}, '', GROPER_ARTICLE_PATH);
                  window.dispatchEvent(new PopStateEvent('popstate'));
                }
              }}
            />

            {/* Time-limited junior colouring-in competition (school holidays).
                Hides itself after COLOURING_CARD_LAST_DAY; see content/colouringComp.ts. */}
            {isColouringCompOpen() && (
              <ColouringCompCard
                onOpen={() => {
                  if (onNavigateToColouring) {
                    onNavigateToColouring();
                  } else {
                    window.history.pushState({}, '', COLOURING_PATH);
                    window.dispatchEvent(new PopStateEvent('popstate'));
                  }
                }}
              />
            )}

            {loading ? (
              <div className="flex justify-center items-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-fishing-blue"></div>
              </div>
            ) : error ? (
              <div className="text-center py-8">
                <p className="text-red-600 mb-4">{error}</p>
                <button 
                  onClick={loadContent}
                  className="bg-fishing-blue hover:bg-blue-700 text-white px-6 py-2 rounded-lg"
                >
                  Try Again
                </button>
              </div>
            ) : announcements.length > 0 ? (
              // DB announcements render in the standard grid below the pinned
              // Groper card, still sorted pinned-first by loadContent. Any DB
              // record with isPinned keeps its small "Pinned" badge here.
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {announcements.map((announcement) => (
                  <div
                    key={announcement.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => onNavigateToAnnouncementDetail?.(announcement.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onNavigateToAnnouncementDetail?.(announcement.id);
                      }
                    }}
                    className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow cursor-pointer flex flex-col h-full"
                  >
                    {announcement.imageUrl && (
                      <StorageImage
                        storageKey={announcement.imageUrl}
                        alt={announcement.title}
                        className="w-full aspect-[16/9] object-cover"
                      />
                    )}
                    <div className="p-4 md:p-5 flex flex-col flex-1">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-xl font-semibold text-fishing-blue">{announcement.title}</h3>
                        {announcement.isPinned && (
                          <span className="bg-fishing-green text-white text-xs px-2 py-1 rounded-full">
                            Pinned
                          </span>
                        )}
                      </div>
                      <p className="text-gray-600 mb-4 flex-1">
                        {announcement.excerpt || announcement.content.substring(0, 220) + '...'}
                      </p>
                      <div className="flex items-center justify-between text-sm text-gray-500">
                        <span className="bg-gray-100 px-2 py-1 rounded text-xs">
                          {announcement.category.replace('_', ' ')}
                        </span>
                        <span>
                          {formatDate(announcement.publishDate)}
                        </span>
                      </div>
                      <span className="mt-4 text-fishing-blue font-semibold text-sm hover:underline self-start">
                        Read more →
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-600">No announcements available at this time.</p>
              </div>
            )}
            <div className="text-center">
              <button
                onClick={() => { window.history.pushState({}, '', '/announcements'); window.dispatchEvent(new PopStateEvent('popstate')); }}
                className="inline-flex min-h-[44px] items-center text-fishing-blue font-medium hover:underline"
              >
                View all announcements →
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Events Section */}
      <section id="events" className="py-6 md:py-8 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-1 text-gray-800">
            Upcoming Events
          </h2>
          {/* #111: times are club (NSW) time on every device; say so. The
              heading block still ends 16px above the list (#117). */}
          <p className="text-sm text-gray-500 text-center mb-4">All times are NSW time.</p>
          
          {loading ? (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-fishing-blue"></div>
            </div>
          ) : visibleEvents.length > 0 ? (
            <ul className="mx-auto max-w-4xl space-y-4" aria-label="Upcoming events">
              {visibleEvents.map((event, i) => (
                <UpcomingEventRow key={event.id} event={event} isNext={i === 0} now={nowTick} />
              ))}
            </ul>
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-600">No upcoming events scheduled at this time.</p>
            </div>
          )}
          <div className="text-center mt-4">
            <button
              onClick={() => { window.history.pushState({}, '', '/events'); window.dispatchEvent(new PopStateEvent('popstate')); }}
              className="inline-flex min-h-[44px] items-center text-fishing-blue font-medium hover:underline"
            >
              View all events →
            </button>
          </div>
        </div>
      </section>

      {/* Sponsors Section */}
      <SponsorBanner />

      {/* Tides & Weather */}
      <section className="py-6 md:py-8 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <TideWeatherWidget className="p-4 md:p-5" />
        </div>
      </section>

      {/* Join Our Club Section */}
      <section className="py-6 md:py-8 px-4 bg-fishing-blue text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-4">Ready to Join Our Community?</h2>
          <p className="text-xl mb-4 text-blue-100">
            Become part of the Tumbulgum Fishing Club family and enjoy exclusive access to events, 
            fishing tips, and a community of passionate anglers.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={onNavigateToRegister}
              className="bg-fishing-green hover:bg-green-600 text-white px-8 py-3 rounded-lg font-semibold transition-colors"
            >
              Start Your Application
            </button>
            <button onClick={onNavigateToLearnMore} className="bg-transparent border-2 border-white hover:bg-white hover:text-fishing-blue text-white px-8 py-3 rounded-lg font-semibold transition-colors">
              Learn More About Membership
            </button>
          </div>
          <p className="text-sm text-blue-200 mt-4">
            Membership applications are reviewed by our admin team. You'll receive an email confirmation once approved.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer id="contact" className="bg-gray-800 text-white py-6 md:py-8 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-3 gap-6">
            <div>
              <h3 className="text-xl font-semibold mb-2">Contact Us</h3>
              <a href="https://www.tumbyfc.com" className="flex items-center gap-2 text-gray-300 hover:text-white transition-colors mb-1">
                <svg className="h-5 w-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.6 9h16.8M3.6 15h16.8M11.5 3a17 17 0 000 18M12.5 3a17 17 0 010 18" />
                </svg>
                www.tumbyfc.com
              </a>
              <a href="mailto:fishing@tumbyfc.com" className="flex items-center gap-2 text-gray-300 hover:text-white transition-colors mb-3">
                <svg className="h-5 w-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                fishing@tumbyfc.com
              </a>
              <div className="flex items-center gap-4">
                <a
                  href="https://www.instagram.com/stories/tumbulgumfishingclub/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Follow us on Instagram"
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                </a>
                <a
                  href="https://www.facebook.com/groups/525214522981589"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Join us on Facebook"
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M22 12.061C22 6.505 17.523 2 12 2S2 6.505 2 12.061c0 5.023 3.657 9.184 8.438 9.939v-7.03H8.078v-2.909h2.36V9.845c0-2.33 1.42-3.62 3.531-3.62 1.01 0 1.877.075 2.129.108v2.464h-1.46c-1.144 0-1.365.544-1.365 1.343v1.762h2.727l-.355 2.909h-2.372V22c4.78-.755 8.437-4.916 8.437-9.939z"/>
                  </svg>
                </a>
              </div>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">Quick Links</h3>
              <ul>
                <li><button onClick={() => { window.history.pushState({}, '', '/learn-more'); window.dispatchEvent(new PopStateEvent('popstate')); }} className="inline-flex min-h-[44px] items-center text-gray-300 hover:text-white">About Us</button></li>
                <li><button onClick={onNavigateToMission} className="inline-flex min-h-[44px] items-center text-gray-300 hover:text-white">Mission, Values & Vision</button></li>
                <li><button onClick={onNavigateToRules} className="inline-flex min-h-[44px] items-center text-gray-300 hover:text-white">Club Rules</button></li>
                <li><button onClick={onNavigateToConduct} className="inline-flex min-h-[44px] items-center text-gray-300 hover:text-white">Code of Conduct</button></li>
                <li><button onClick={onNavigateToWaiver} className="inline-flex min-h-[44px] items-center text-gray-300 hover:text-white">Waiver & Release</button></li>
                <li><a href="/Constitution-Tumbulgum-Fishing-Club-V5.pdf" target="_blank" rel="noopener noreferrer" className="inline-flex min-h-[44px] items-center text-gray-300 hover:text-white">Club Constitution</a></li>
                <li><button className="inline-flex min-h-[44px] items-center text-gray-300 hover:text-white" onClick={() => { window.history.pushState({}, '', '/regulations'); window.dispatchEvent(new PopStateEvent('popstate')); }}>NSW Fishing Regulations</button></li>
                <li><button className="inline-flex min-h-[44px] items-center text-gray-300 hover:text-white" onClick={() => { window.history.pushState({}, '', '/privacy'); window.dispatchEvent(new PopStateEvent('popstate')); }}>Privacy Policy</button></li>
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">Location</h3>
              <p className="text-gray-300 mb-1">Tumbulgum, New South Wales</p>
              <p className="text-gray-300 mb-2">Australia</p>
              <div className="mt-2 rounded-lg overflow-hidden border border-gray-700">
                {import.meta.env.VITE_LOCATION_API_KEY ? (
                  <img
                    src={`https://maps.geo.ap-southeast-2.amazonaws.com/v2/static/map?style=Standard&width=400&height=150&zoom=13&center=153.4833,-28.2667&key=${import.meta.env.VITE_LOCATION_API_KEY}`}
                    width="100%"
                    height="150"
                    alt="Map showing Tumbulgum, NSW location"
                    loading="lazy"
                    style={{ objectFit: 'cover' }}
                  />
                ) : (
                  <div className="bg-gray-700 flex items-center justify-center" style={{ height: 150 }}>
                    <p className="text-gray-400 text-sm">Map unavailable</p>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="border-t border-gray-700 mt-6 pt-4 text-center">
            <p className="text-gray-400">&copy; 2026 Tumbulgum Fishing Club. All rights reserved.</p>
            <p className="mt-2">
              <a
                href="/privacy"
                onClick={(e) => { e.preventDefault(); window.history.pushState({}, '', '/privacy'); window.dispatchEvent(new PopStateEvent('popstate')); }}
                className="text-gray-500 hover:text-gray-300 text-sm"
              >Privacy Policy</a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;